import { toast } from "sonner";
import { api } from "@/lib/api";

async function startReauth() {
  try {
    const r = await api.get("/auth/google/start");
    window.location.href = r.data.authorization_url;
  } catch {
    toast.error("Could not start re-authorization");
  }
}

/**
 * Inspect an axios error and show a targeted toast. Recognized codes:
 * - REAUTH_REQUIRED → toast with Re-authorize action
 * - MISSING_SCOPE → toast with Re-authorize action (grant new permission)
 * - RATE_LIMITED → toast telling the user how long to wait
 * Returns true if a specific handler was shown; false otherwise.
 */
export function handleReauth(error, fallbackMessage = "Something went wrong") {
  const status = error?.response?.status;
  const detail = error?.response?.data?.detail;
  const code = typeof detail === "object" ? detail.code : null;

  if (status === 401 && code === "REAUTH_REQUIRED") {
    toast.error("Google authorization expired", {
      description: detail?.message || "Sign in again to reconnect ContactAssist to Google.",
      duration: 10000,
      action: { label: "Re-authorize", onClick: startReauth },
    });
    return true;
  }

  if (status === 400 && code === "MISSING_SCOPE") {
    toast.error("Extra Google permission needed", {
      description: detail?.message || "Sign in again to grant this permission.",
      duration: 10000,
      action: { label: "Re-authorize", onClick: startReauth },
    });
    return true;
  }

  if (status === 429 && code === "RATE_LIMITED") {
    const wait = detail?.retry_after_seconds ?? 0;
    toast.warning("Slow down a bit", {
      description: detail?.message || `Please wait ${wait}s before trying again.`,
      duration: 6000,
    });
    return true;
  }

  if (status === 402 && code === "PAYWALL") {
    toast.error("Trial or subscription required", {
      description: detail?.message || "Start your 7-day free trial to run scans and use AI features.",
      duration: 10000,
      action: {
        label: "See pricing",
        onClick: () => { window.location.href = "/pricing"; },
      },
    });
    return true;
  }

  const msg = typeof detail === "string" ? detail : fallbackMessage;
  toast.error(msg);
  return false;
}
